import { useMutation } from "@apollo/client";
import { type ReactNode, useEffect, useState } from "react";
import { v4 as uuid } from "uuid";
import { Camera as CameraIcon, Check as CheckIcon, ChevronRight as ChevronRightIcon } from "@tamagui/lucide-icons";
import { appear, Body2, Button, Card, type CardProps, getTokenValue, Icon, Image, Loader, LottieAnimation, Subtitle1, XStack, YStack } from "@medbillai/ui";
import { UploadDocumentPickerProvider, useRequiredUploadDocumentPicker } from "../../components/upload-document-sheet/UploadDocumentPickerProvider";
import { captureUnreportedError } from "../../lib/apollo/error";
import { type UploadFile, useDocumentUpload } from "../../provider/documents";
import sweepSource from "./CardReadSweep.json";
import { readMemberCardMutation } from "./utils/queries";

/** Past this the user types the ID; the read keeps going server-side. */
export const CARD_READ_WAIT_MS = 10_000;
type Props = {
  onUpload: (storedDocumentId: Promise<string | undefined>) => void;
  onRead: (memberId: string | null) => void;
  onRemove: () => void;
};
type Phase = "idle" | "reading" | "found" | "failed";
export function MemberCardScan(props: Props) {
  return <UploadDocumentPickerProvider entryPoint="OTHER" data-sentry-element="UploadDocumentPickerProvider" data-sentry-component="MemberCardScan" data-sentry-source-file="MemberCardScan.tsx">
      <CardPhotoRow {...props} data-sentry-element="CardPhotoRow" data-sentry-source-file="MemberCardScan.tsx" />
    </UploadDocumentPickerProvider>;
}
function CardPhotoRow({
  onUpload,
  onRead,
  onRemove
}: Props) {
  const openPicker = useRequiredUploadDocumentPicker();
  const {
    upload
  } = useDocumentUpload();
  const [readMemberCard] = useMutation(readMemberCardMutation);
  const [phase, setPhase] = useState<Phase>("idle");
  const [photo, setPhoto] = useState<Blob>();
  const thumbnail = useDataUrl(photo);
  const read = async (documentId: string): Promise<string | null> => {
    const timeout = new Promise<null>(resolve => setTimeout(() => resolve(null), CARD_READ_WAIT_MS));
    const result = readMemberCard({
      variables: {
        documentId
      }
    }).then(({
      data
    }) => data?.readMemberCard?.memberId ?? null, (error: unknown) => {
      captureUnreportedError(error);
      return null;
    });
    return await Promise.race([result, timeout]);
  };
  const fail = () => {
    setPhase("failed");
    setPhoto(undefined);
  };
  const send = async (files: UploadFile[]) => {
    setPhoto(files[0]?.blob);
    setPhase("reading");
    let memberId: string | null = null;
    let stored: (documentId: string | undefined) => void = () => undefined;
    onUpload(new Promise(resolve => {
      stored = resolve;
    }));
    try {
      await upload({
        key: `member-card:${uuid()}`,
        uploadFiles: files,
        dataType: "memberCard",
        userVisible: false,
        startAnalysis: false,
        onUploadStarted: () => undefined,
        onUploadComplete: async documentId => {
          stored(documentId);
          memberId = await read(documentId);
        },
        onUploadError: () => undefined
      });
    } finally {
      stored(undefined);
    }
    if (memberId === null) {
      fail();
    } else {
      setPhase("found");
    }
    onRead(memberId);
  };
  const pick = () => openPicker({
    title: "Photograph the front of your card",
    filePickerOptions: {
      multiple: false
    },
    onFilesLoaded: send,
    onUploadError: () => {
      fail();
      onRead(null);
    }
  });
  const remove = () => {
    setPhase("idle");
    setPhoto(undefined);
    onRemove();
  };
  return phase === "reading" || phase === "found" ? <PhotoRow thumbnail={thumbnail} found={phase === "found"} onRemove={remove} data-sentry-element="PhotoRow" data-sentry-component="CardPhotoRow" data-sentry-source-file="MemberCardScan.tsx" /> : <CameraRow failed={phase === "failed"} onPress={pick} data-sentry-element="CameraRow" data-sentry-component="CardPhotoRow" data-sentry-source-file="MemberCardScan.tsx" />;
}

/** A data URL rather than an object URL: React Native's Image can't load `blob:`. */
function useDataUrl(blob: Blob | undefined) {
  const [url, setUrl] = useState<string>();
  useEffect(() => {
    setUrl(undefined);
    if (blob === undefined) {
      return;
    }
    let live = true;
    const reader = new FileReader();
    reader.onload = () => {
      if (live && typeof reader.result === "string") {
        setUrl(reader.result);
      }
    };
    reader.readAsDataURL(blob);
    return () => {
      live = false;
      reader.abort();
    };
  }, [blob]);
  return url;
}
function RowCard({
  children,
  ...card
}: Pick<CardProps, "onPress" | "testID" | "paddingLeft"> & {
  children: ReactNode;
}) {
  return <Card size="small" group minHeight="$si9" paddingVertical="$sp1.5" justifyContent="center" {...card} data-sentry-element="Card" data-sentry-component="RowCard" data-sentry-source-file="MemberCardScan.tsx">
      <XStack alignItems="center" gap="$sp1.5" data-sentry-element="XStack" data-sentry-source-file="MemberCardScan.tsx">
        {children}
      </XStack>
    </Card>;
}
function RowText({
  title,
  subtitle
}: {
  title: string;
  subtitle: string;
}) {
  return <YStack flex={1} minWidth={0} gap="$sp0.25" data-sentry-element="YStack" data-sentry-component="RowText" data-sentry-source-file="MemberCardScan.tsx">
      <Subtitle1 data-sentry-element="Subtitle1" data-sentry-source-file="MemberCardScan.tsx">{title}</Subtitle1>
      <Body2 color="$textSubtle" data-sentry-element="Body2" data-sentry-source-file="MemberCardScan.tsx">{subtitle}</Body2>
    </YStack>;
}
function CameraRow({
  failed,
  onPress
}: {
  failed: boolean;
  onPress: () => void;
}) {
  return <RowCard testID="member-id.scan-card" onPress={onPress} data-sentry-element="RowCard" data-sentry-component="CameraRow" data-sentry-source-file="MemberCardScan.tsx">
      <YStack width="$si5.5" height="$si5.5" flexShrink={0} borderRadius="$br1" backgroundColor="$surface0"
    // The card hovers and presses to this same fill; a sticky touch hover would hide the tile.
    $group-hover={{
      backgroundColor: "$surface0Hover"
    }} $group-press={{
      backgroundColor: "$surface0Hover"
    }} alignItems="center" justifyContent="center" data-sentry-element="YStack" data-sentry-source-file="MemberCardScan.tsx">
        <Icon icon={CameraIcon} size="$iconLarge" color="$iconDefault" data-sentry-element="Icon" data-sentry-source-file="MemberCardScan.tsx" />
      </YStack>
      {failed ? <RowText title="Couldn't read the ID" subtitle="Type it in, or try another photo" /> : <RowText title="Take a photo of your card" subtitle="We'll read the ID off it" />}
      <Icon icon={ChevronRightIcon} size="$iconMedium" color="$iconSubtle" data-sentry-element="Icon" data-sentry-source-file="MemberCardScan.tsx" />
    </RowCard>;
}
function PhotoRow({
  thumbnail,
  found,
  onRemove
}: {
  thumbnail: string | undefined;
  found: boolean;
  onRemove: () => void;
}) {
  return <RowCard paddingLeft="$sp1.5" data-sentry-element="RowCard" data-sentry-component="PhotoRow" data-sentry-source-file="MemberCardScan.tsx">
      <Thumbnail uri={thumbnail} found={found} data-sentry-element="Thumbnail" data-sentry-source-file="MemberCardScan.tsx" />
      {found ? <RowText title="Insurance card" subtitle="Member ID found" /> : <RowText title="Reading your card" subtitle="This takes a second" />}
      {found ? <Button variant="tertiary" size="medium" fillWidth={false} flexShrink={0} onPress={onRemove}>
          Remove
        </Button> : <Loader size="$iconSmall" color="$iconSubtle" />}
    </RowCard>;
}
const THUMB_WIDTH = "$si9";
const THUMB_HEIGHT = "$si5.5";
function Thumbnail({
  uri,
  found
}: {
  uri?: string;
  found: boolean;
}) {
  return <YStack width={THUMB_WIDTH} height={THUMB_HEIGHT} flexShrink={0} borderRadius="$br1" overflow="hidden" backgroundColor="$surface0Hover" data-sentry-element="YStack" data-sentry-component="Thumbnail" data-sentry-source-file="MemberCardScan.tsx">
      {uri === undefined ? null : <Image src={uri} width="100%" height="100%" objectFit="cover" />}
      {found ? <FoundBadge /> : <ReadingSweep />}
    </YStack>;
}
function ReadingSweep() {
  return <YStack position="absolute" top={0} left={0} pointerEvents="none" data-sentry-element="YStack" data-sentry-component="ReadingSweep" data-sentry-source-file="MemberCardScan.tsx">
      <LottieAnimation source={sweepSource} width={Number(getTokenValue(THUMB_WIDTH, "size"))} height={Number(getTokenValue(THUMB_HEIGHT, "size"))} data-sentry-element="LottieAnimation" data-sentry-source-file="MemberCardScan.tsx" />
    </YStack>;
}
function FoundBadge() {
  return <YStack position="absolute" right="$sp0.5" bottom="$sp0.5" width="$si2.5" height="$si2.5" borderRadius="$brFull" backgroundColor="$bgPositiveStrong" alignItems="center" justifyContent="center" {...appear("scale")} data-sentry-element="YStack" data-sentry-component="FoundBadge" data-sentry-source-file="MemberCardScan.tsx">
      <Icon icon={CheckIcon} size="$iconXSmall" color="$iconInverse" data-sentry-element="Icon" data-sentry-source-file="MemberCardScan.tsx" />
    </YStack>;
}